
import { UserRole } from '@/types/userRole';

export type TicketPriority = 'low' | 'medium' | 'high';

export interface SupportTicket {
  id: string;
  caseNumber: string;
  subject: string;
  message: string;
  content?: string; // Added for compatibility with other parts of the codebase
  status: 'open' | 'closed' | 'pending' | 'resolved';
  priority: TicketPriority;
  userId: string;
  userName: string;
  userEmail: string;
  userRole: string;
  createdAt: Date;
  updatedAt: Date;
  instanceId?: string;
  replies?: TicketReply[];
}

export interface TicketReply {
  id: string;
  content: string;
  userId: string;
  userName: string;
  userRole: string;
  userEmail: string;
  createdAt: Date;
}

let tickets: SupportTicket[] = [];
let ticketCounter = 1000;

export const getTickets = async (userRole: UserRole | string): Promise<SupportTicket[]> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));

  // If superadmin or admin, return all tickets
  if (userRole === 'superadmin' || userRole === 'admin' || userRole === 'masterAdmin') {
    return [...tickets];
  }

  // Otherwise, filter by user role (in a real app, we'd filter by user ID)
  return tickets.filter(ticket => ticket.userRole === userRole);
};

export const getTicket = async (ticketId: string): Promise<SupportTicket | null> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 300));

  const ticket = tickets.find(t => t.id === ticketId);
  return ticket || null;
};

export interface TicketFormData {
  subject: string;
  priority: TicketPriority;
  content: string;
  instanceId?: string;
}

// Add this function for compatibility with other parts of the codebase
export const getSupportMessageLevel = (userRole: UserRole | string): 'user' | 'admin' | 'system' => {
  if (userRole === 'superadmin' || userRole === 'admin' || userRole === 'masterAdmin') {
    return 'admin';
  } else if (userRole === 'system') {
    return 'system';
  }
  return 'user';
};

export const createTicket = async (
  ticketData: TicketFormData,
  userRole: UserRole | string,
  userId?: string,
  userName?: string,
  userEmail?: string
): Promise<SupportTicket> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 700));

  ticketCounter++;
  const caseNumber = `SUP-${ticketCounter}`;

  const newTicket: SupportTicket = {
    id: `ticket-${Date.now()}`,
    caseNumber,
    subject: ticketData.subject,
    message: ticketData.content,
    content: ticketData.content, // Add for compatibility
    status: 'open',
    priority: ticketData.priority,
    userId: userId || 'anonymous',
    userName: userName || 'Anonymous User',
    userEmail: userEmail || 'anonymous@example.com',
    userRole: userRole.toString(),
    createdAt: new Date(),
    updatedAt: new Date(),
    instanceId: ticketData.instanceId,
    replies: []
  };

  tickets = [newTicket, ...tickets];
  return newTicket;
};

export const closeTicket = async (ticketId: string): Promise<SupportTicket> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));

  const ticketIndex = tickets.findIndex(t => t.id === ticketId);
  if (ticketIndex === -1) {
    throw new Error('Ticket not found');
  }

  const updatedTicket = {
    ...tickets[ticketIndex],
    status: 'closed' as const,
    updatedAt: new Date()
  };

  tickets[ticketIndex] = updatedTicket;
  return updatedTicket;
};

export const addReplyToTicket = async (
  ticketId: string,
  reply: TicketReply
): Promise<SupportTicket> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));

  const ticketIndex = tickets.findIndex(t => t.id === ticketId);
  if (ticketIndex === -1) {
    throw new Error('Ticket not found');
  }

  if (!tickets[ticketIndex].replies) {
    tickets[ticketIndex].replies = [];
  }

  tickets[ticketIndex].replies!.push(reply);
  tickets[ticketIndex].status = 'pending';
  tickets[ticketIndex].updatedAt = new Date();

  return tickets[ticketIndex];
};

export const resolveTicket = async (ticketId: string): Promise<SupportTicket> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));

  const ticketIndex = tickets.findIndex(t => t.id === ticketId);
  if (ticketIndex === -1) {
    throw new Error('Ticket not found');
  }

  const updatedTicket = {
    ...tickets[ticketIndex],
    status: 'resolved' as const,
    updatedAt: new Date()
  };

  tickets[ticketIndex] = updatedTicket;
  return updatedTicket;
};

export const reopenTicket = async (ticketId: string): Promise<SupportTicket> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));

  const ticketIndex = tickets.findIndex(t => t.id === ticketId);
  if (ticketIndex === -1) {
    throw new Error('Ticket not found');
  }

  const updatedTicket = {
    ...tickets[ticketIndex],
    status: 'open' as const,
    updatedAt: new Date()
  };

  tickets[ticketIndex] = updatedTicket;
  return updatedTicket;
};

// Additional functions needed by other components
export const getUserTickets = getTickets; // Alias for getTickets
export const replyToTicket = addReplyToTicket; // Alias for addReplyToTicket

export const getTicketPermissions = (userRole: UserRole | string) => {
  return {
    canResolve: userRole === 'admin' || userRole === 'superadmin' || userRole === 'masterAdmin',
    canClose: userRole === 'admin' || userRole === 'superadmin' || userRole === 'masterAdmin',
    canReopen: userRole === 'admin' || userRole === 'superadmin' || userRole === 'masterAdmin',
    canManage: userRole === 'admin' || userRole === 'superadmin' || userRole === 'masterAdmin',
  };
};

export const formatCentralTime = (date: Date): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Chicago'
  };
  return new Date(date).toLocaleString('en-US', options);
};

// Create mock data
(() => {
  const mockTickets: SupportTicket[] = [
    {
      id: 'ticket-1',
      caseNumber: 'SUP-1001',
      subject: 'Cannot access my account',
      message: 'I am unable to log in to my account since yesterday. I have tried resetting my password but that didn\'t help.',
      content: 'I am unable to log in to my account since yesterday. I have tried resetting my password but that didn\'t help.',
      status: 'open',
      priority: 'high',
      userId: 'user-1',
      userName: 'John Doe',
      userEmail: 'john.doe@example.com',
      userRole: 'user',
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      replies: [
        {
          id: 'reply-1',
          content: 'Have you tried clearing your browser cache?',
          userId: 'admin-1',
          userName: 'Admin User',
          userEmail: 'admin@example.com',
          userRole: 'admin',
          createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
        },
        {
          id: 'reply-2',
          content: 'Yes, I tried that but it didn\'t help.',
          userId: 'user-1',
          userName: 'John Doe',
          userEmail: 'john.doe@example.com',
          userRole: 'user',
          createdAt: new Date(Date.now() - 0.5 * 24 * 60 * 60 * 1000)
        }
      ]
    },
    {
      id: 'ticket-2',
      caseNumber: 'SUP-1002',
      subject: 'Feature request: Dark mode',
      message: 'Would it be possible to add a dark mode to the application? It would be much easier on the eyes when working at night.',
      status: 'pending',
      priority: 'medium',
      userId: 'user-2',
      userName: 'Jane Smith',
      userEmail: 'jane.smith@example.com',
      userRole: 'user',
      createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
      updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
      replies: [
        {
          id: 'reply-3',
          content: 'Thank you for your suggestion. We have added it to our roadmap and plan to implement it in the next quarter.',
          userId: 'admin-1',
          userName: 'Admin User',
          userEmail: 'admin@example.com',
          userRole: 'admin',
          createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
        }
      ]
    },
    {
      id: 'ticket-3',
      caseNumber: 'SUP-1003',
      subject: 'Billing issue',
      message: 'I was charged twice for my subscription this month. Please help resolve this issue.',
      status: 'open',
      priority: 'high',
      userId: 'business-1',
      userName: 'Business User',
      userEmail: 'business@example.com',
      userRole: 'business',
      createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      instanceId: 'saas-1',
      replies: []
    }
  ];

  tickets = mockTickets;
})();
